import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { GraduationCap, CheckCircle, CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import ReactSelect from 'react-select';

const GRADE_OPTIONS = [
  "ป.1",
  "ป.2",
  "ป.3",
  "ป.4",
  "ป.5",
  "ป.6",
  "ม.1",
  "ม.2",
  "ม.3",
  "ม.4",
  "ม.5",
  "ม.6",
  "มหาวิทยาลัย",
];

const SUBJECT_OPTIONS = [
  "คณิตศาสตร์",
  "เคมี",
  "ชีววิทยา",
  "ฟิสิกส์",
  "ภาษาจีน",
  "ภาษาไทย",
  "วิทยาศาสตร์",
  "สังคม",
  "อังกฤษ",
  "อื่นๆ",
];

const COURSE_OPTIONS = ["Premier Class TH", "Premier Class Inter", "Group Class", "อื่นๆ"];

const LEARNING_FORMAT_OPTIONS = ["ออนไลน์", "ออนไซต์", "ออนไลน์/ออนไซต์"];

const TEACHING_STYLE_OPTIONS = [
  "ครูที่สามารถละลายพฤติกรรม",
  "สอนแบบจริงจัง เข้มงวด",
  "ครูต้องให้ Mindset ในการเรียนของน้องได้",
  "ครูที่สามารถดึงความสนใจน้องได้",
  "ครูที่มีเทคนิคการสอน",
  "ครูที่มีพลังบวก คอยชื่นชมน้อง เพิ่มความมั่นใจให้กับน้อง",
  "ใจเย็น มีเหตุผล เข้าใจธรรมชาติของผู้เรียน",
];

const PARENT_CONCERNS_OPTIONS = [
  "ไม่ค่อยพูด เป็นเด็กขี้อาย ไม่กล้าถาม",
  "ไม่ค่อยมีแพชชั่นในการเรียน",
  "โฟกัสอะไรนานๆไม่ได้ ชวนครูคุยออกนอกเรื่อง",
  "ไม่ค่อยจำ ลืมง่าย",
  "ไม่ค่อยชอบเรียนเนื้อหายากๆ",
  "ต้องการฝึกทำโจทย์ เตรียมสอบเข้า",
];

const LOCATION_OPTIONS = ["ออนไลน์", "งามวงศ์วาน", "บรรทัดทอง"];

const PARENT_CONTACT_CHANNELS = ["เพจ Facebook", "LINE", "โทรศัพท์", "อื่นๆ"];

interface FormData {
  student_firstname: string;
  student_lastname: string;
  student_nickname: string;
  grade: string;
  school: string;
  student_phone: string;
  subject: string;
  other_subject: string;
  language: string;
  learning_format: string;
  location: string;
  trial_day: string;
  trial_time: string;
  convenient_time: string;
  inconvenient_time: string;
  inconvenient_reason: string;
  parent_contact_channel: string;
  parent_name: string;
  parent_phone: string;
  teaching_style: string[];
  parent_concerns: string[];
  parent_concerns_other: string;
  parent_expectations_trial: string;
  parent_expectations_fishsix: string;
  desired_course: string;
  desired_hours: string;
  desired_start_date: string;
  additional_pain_points: string;
  specialized_tutoring: string[];
}

const initialFormData: FormData = {
  student_firstname: "",
  student_lastname: "",
  student_nickname: "",
  grade: "",
  school: "",
  student_phone: "",
  subject: "",
  other_subject: "",
  language: "ไทย",
  learning_format: "",
  location: "",
  trial_day: "",
  trial_time: "",
  convenient_time: "",
  inconvenient_time: "",
  inconvenient_reason: "",
  parent_contact_channel: "",
  parent_name: "",
  parent_phone: "",
  teaching_style: [],
  parent_concerns: [],
  parent_concerns_other: "",
  parent_expectations_trial: "",
  parent_expectations_fishsix: "",
  desired_course: "",
  desired_hours: "",
  desired_start_date: "",
  additional_pain_points: "",
  specialized_tutoring: [],
};

const PublicTrialFormPage = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [specializedTutoringOptions, setSpecializedTutoringOptions] = useState<string[]>([]);

  useEffect(() => {
    const fetchOptions = async () => {
      const { data } = await supabase.from('specialized_tutorings').select('name').order('name');
      if (data) setSpecializedTutoringOptions(data.map((d: any) => d.name));
    };
    fetchOptions();
  }, []);

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleTeachingStyleToggle = (style: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      teaching_style: checked ? [...prev.teaching_style, style] : prev.teaching_style.filter((s) => s !== style),
    }));
  };

  const handleParentConcernToggle = (concern: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      parent_concerns: checked ? [...prev.parent_concerns, concern] : prev.parent_concerns.filter((c) => c !== concern),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.student_firstname.trim() || !formData.grade || !formData.subject) {
      toast({
        title: "กรุณากรอกข้อมูลที่จำเป็น",
        description: "ชื่อนักเรียน, ระดับชั้น, และวิชาที่ต้องการ",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const studentName = `${formData.student_firstname} ${formData.student_lastname}`.trim();
      const now = new Date();

      // Single-select subject: if "อื่นๆ", use other_subject text
      const subjectString =
        formData.subject === "อื่นๆ" && formData.other_subject.trim()
          ? formData.other_subject.trim()
          : formData.subject;

      const insertData = {
        student_firstname: formData.student_firstname.trim(),
        student_lastname: formData.student_lastname.trim(),
        student_nickname: formData.student_nickname.trim(),
        student_name: studentName,
        grade: formData.grade,
        school: formData.school.trim(),
        student_phone: formData.student_phone.trim() || null,
        subject: subjectString,
        other_subject: formData.subject === "อื่นๆ" ? formData.other_subject.trim() : null,
        language: formData.language,
        learning_format: formData.learning_format,
        location: formData.location,
        trial_time: [formData.trial_day.trim(), formData.trial_time.trim()].filter(Boolean).join(' ') || null,
        convenient_time: formData.convenient_time.trim() || null,
        inconvenient_time: formData.inconvenient_time.trim() || null,
        inconvenient_reason: formData.inconvenient_reason.trim() || null,
        parent_contact_channel: formData.parent_contact_channel || null,
        parent_name: formData.parent_name.trim() || null,
        parent_phone: formData.parent_phone.trim() || null,
        teaching_style: formData.teaching_style.length > 0 ? formData.teaching_style : null,
        parent_concerns: (() => {
          const allConcerns = [...formData.parent_concerns];
          if (formData.parent_concerns_other.trim()) allConcerns.push(formData.parent_concerns_other.trim());
          return allConcerns.length > 0 ? JSON.stringify(allConcerns) : null;
        })(),
        parent_expectations_trial: formData.parent_expectations_trial.trim(),
        parent_expectations_fishsix: formData.parent_expectations_fishsix.trim(),
        desired_course: formData.desired_course.trim(),
        desired_hours: formData.desired_hours ? Number(formData.desired_hours) : null,
        desired_start_date: formData.desired_start_date || null,
        additional_pain_points: formData.additional_pain_points.trim(),
        specialized_tutoring: formData.specialized_tutoring.length > 0 ? JSON.stringify(formData.specialized_tutoring) : null,
        status: "ลูกค้ากรอกฟอร์มแล้ว รอ OP หาครู",
        month: now.getMonth() + 1,
        year: now.getFullYear(),
      };
      const { error } = await supabase.from("class_trials").insert([insertData] as any);

      if (error) throw error;

      setIsSubmitted(true);
      toast({ title: "ส่งข้อมูลสำเร็จ", description: "ทีมงานจะติดต่อกลับเร็วๆ นี้" });
    } catch (err) {
      console.error("Error submitting trial form:", err);
      toast({ title: "เกิดข้อผิดพลาด", description: "ไม่สามารถส่งข้อมูลได้ กรุณาลองอีกครั้ง", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-secondary flex items-center justify-center p-4">
        <Card className="max-w-lg w-full text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <CheckCircle className="w-16 h-16 mx-auto text-emerald-500" />
            <h2 className="text-2xl font-bold text-foreground">ส่งข้อมูลเรียบร้อยแล้ว!</h2>
            <p className="text-muted-foreground">
              ขอบคุณที่สนใจทดลองเรียนกับเรา ทีมงานจะติดต่อกลับเพื่อนัดวันทดลองเรียนเร็วๆ นี้
            </p>
            <Button
              onClick={() => {
                setIsSubmitted(false);
                setFormData(initialFormData);
              }}
              variant="outline"
            >
              กรอกฟอร์มอีกครั้ง
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary">
      <header className="bg-primary text-primary-foreground py-6 shadow-md">
        <div className="container max-w-3xl mx-auto px-4 flex items-center gap-3">
          <GraduationCap className="w-8 h-8" />
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">ฟอร์มสมัครทดลองเรียน</h1>
            <p className="mt-1 text-primary-foreground/80 text-sm">กรุณากรอกข้อมูลเพื่อให้ทีมงานนัดวันทดลองเรียน</p>
          </div>
        </div>
      </header>

      <main className="container max-w-3xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Student Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">ข้อมูลนักเรียน</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstname">
                    ชื่อ <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="firstname"
                    value={formData.student_firstname}
                    onChange={(e) => handleChange("student_firstname", e.target.value)}
                    placeholder="ชื่อนักเรียน"
                    required
                    maxLength={100}
                  />
                </div>
                <div>
                  <Label htmlFor="lastname">นามสกุล</Label>
                  <Input
                    id="lastname"
                    value={formData.student_lastname}
                    onChange={(e) => handleChange("student_lastname", e.target.value)}
                    placeholder="นามสกุลนักเรียน"
                    maxLength={100}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nickname">ชื่อเล่น</Label>
                  <Input
                    id="nickname"
                    value={formData.student_nickname}
                    onChange={(e) => handleChange("student_nickname", e.target.value)}
                    placeholder="ชื่อเล่น"
                    maxLength={50}
                  />
                </div>
                <div>
                  <Label htmlFor="school">โรงเรียน</Label>
                  <Input
                    id="school"
                    value={formData.school}
                    onChange={(e) => handleChange("school", e.target.value)}
                    placeholder="ชื่อโรงเรียน"
                    maxLength={200}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="student_phone">เบอร์โทรติดต่อนักเรียน</Label>
                <Input
                  id="student_phone"
                  type="tel"
                  value={formData.student_phone}
                  onChange={(e) => handleChange("student_phone", e.target.value)}
                  placeholder="0xx-xxx-xxxx"
                  maxLength={20}
                />
              </div>
              <div>
                <Label>
                  ระดับชั้น <span className="text-destructive">*</span>
                </Label>
                <Select value={formData.grade} onValueChange={(v) => handleChange("grade", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกระดับชั้น" />
                  </SelectTrigger>
                  <SelectContent>
                    {GRADE_OPTIONS.map((g) => (
                      <SelectItem key={g} value={g}>
                        {g}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Single-select subject */}
              <div>
                <Label>
                  วิชาที่ต้องการ <span className="text-destructive">*</span>
                </Label>
                <Select value={formData.subject} onValueChange={(v) => handleChange("subject", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกวิชา" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUBJECT_OPTIONS.map((subject) => (
                      <SelectItem key={subject} value={subject}>
                        {subject}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.subject === "อื่นๆ" && (
                <div>
                  <Label htmlFor="other_subject">ระบุวิชา</Label>
                  <Input
                    id="other_subject"
                    value={formData.other_subject}
                    onChange={(e) => handleChange("other_subject", e.target.value)}
                    placeholder="ระบุวิชาที่ต้องการ"
                    maxLength={100}
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Parent Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">ข้อมูลผู้ปกครอง</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>ช่องทางติดต่อผู้ปกครอง</Label>
                <Select
                  value={formData.parent_contact_channel}
                  onValueChange={(v) => handleChange("parent_contact_channel", v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกช่องทาง" />
                  </SelectTrigger>
                  <SelectContent>
                    {PARENT_CONTACT_CHANNELS.map((ch) => (
                      <SelectItem key={ch} value={ch}>
                        {ch}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="parent_name">ชื่อผู้ปกครองที่ทักเข้ามา</Label>
                  <Input
                    id="parent_name"
                    value={formData.parent_name}
                    onChange={(e) => handleChange("parent_name", e.target.value)}
                    placeholder="ชื่อผู้ปกครอง"
                    maxLength={100}
                  />
                </div>
                <div>
                  <Label htmlFor="parent_phone">เบอร์โทร</Label>
                  <Input
                    id="parent_phone"
                    type="tel"
                    value={formData.parent_phone}
                    onChange={(e) => handleChange("parent_phone", e.target.value)}
                    placeholder="0xx-xxx-xxxx"
                    maxLength={20}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Learning Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">รูปแบบการเรียนที่ต้องการ</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>ระบุทั้งเนื้อหาและภาษาที่สื่อสาร</Label>
                <Input
                  value={formData.language}
                  onChange={(e) => handleChange("language", e.target.value)}
                  placeholder="เช่น เนื้อหาอังกฤษ สื่อสารไทย / เนื้อหาไทยสื่อสารไทย"
                  maxLength={200}
                />
              </div>
              {specializedTutoringOptions.length > 0 && (
                <div>
                  <Label>ต้องการเรียนเฉพาะทาง</Label>
                  <p className="text-xs text-muted-foreground mb-2">เลือกได้มากกว่า 1 ข้อ</p>
                  <ReactSelect
                    isMulti
                    options={specializedTutoringOptions.map((o) => ({ value: o, label: o }))}
                    value={formData.specialized_tutoring.map((v) => ({ value: v, label: v }))}
                    onChange={(selected) => {
                      setFormData((prev) => ({
                        ...prev,
                        specialized_tutoring: selected ? selected.map((s: any) => s.value) : [],
                      }));
                    }}
                    placeholder="เลือกเฉพาะทาง..."
                    noOptionsMessage={() => "ไม่พบตัวเลือก"}
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({ ...base, minHeight: '40px', borderColor: 'hsl(var(--border))', borderRadius: 'calc(var(--radius) - 2px)' }),
                      menu: (base) => ({ ...base, zIndex: 50 }),
                    }}
                  />
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>รูปแบบการเรียน</Label>
                  <Select value={formData.learning_format} onValueChange={(v) => handleChange("learning_format", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="เลือกรูปแบบ" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEARNING_FORMAT_OPTIONS.map((f) => (
                        <SelectItem key={f} value={f}>
                          {f}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>สถานที่</Label>
                  <Select value={formData.location} onValueChange={(v) => handleChange("location", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="เลือกสถานที่" />
                    </SelectTrigger>
                    <SelectContent>
                      {LOCATION_OPTIONS.map((l) => (
                        <SelectItem key={l} value={l}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {/* ทดลองเรียน Section */}
              <div className="space-y-3 rounded-lg border border-border p-4">
                <h3 className="font-semibold text-foreground">ทดลองเรียน</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>วันที่ต้องการทดลองเรียน</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn("w-full justify-start text-left font-normal", !formData.trial_day && "text-muted-foreground")}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.trial_day ? format(new Date(formData.trial_day + 'T00:00:00'), 'dd/MM/yyyy') : 'เลือกวันที่'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.trial_day ? new Date(formData.trial_day + 'T00:00:00') : undefined}
                          onSelect={(d) => handleChange("trial_day", d ? format(d, 'yyyy-MM-dd') : '')}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label htmlFor="trial_time">เวลาที่ต้องการทดลองเรียน</Label>
                    <Input
                      id="trial_time"
                      value={formData.trial_time}
                      onChange={(e) => handleChange("trial_time", e.target.value)}
                      placeholder="เช่น 10:00 - 12:00"
                      maxLength={200}
                    />
                  </div>
                </div>
              </div>

              {/* เวลาเรียนจริง Section */}
              <div className="space-y-3 rounded-lg border border-border p-4">
                <h3 className="font-semibold text-foreground">เวลาเรียนจริง</h3>
                <div>
                  <Label htmlFor="convenient_time">วันและเวลาที่ต้องการเรียนจริง</Label>
                  <Input
                    id="convenient_time"
                    value={formData.convenient_time}
                    onChange={(e) => handleChange("convenient_time", e.target.value)}
                    placeholder="เช่น วันพุธ เวลา 11.00 - 13.00 น."
                    maxLength={500}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="inconvenient_time">ช่วงเวลาที่ไม่สะดวก</Label>
                    <Input
                      id="inconvenient_time"
                      value={formData.inconvenient_time}
                      onChange={(e) => handleChange("inconvenient_time", e.target.value)}
                      placeholder="เช่น วันพุธ ทั้งวัน"
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <Label htmlFor="inconvenient_reason">เหตุผลที่ไม่สะดวก</Label>
                    <Input
                      id="inconvenient_reason"
                      value={formData.inconvenient_reason}
                      onChange={(e) => handleChange("inconvenient_reason", e.target.value)}
                      placeholder="เช่น มีเรียนพิเศษ"
                      maxLength={200}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="desired_hours">จำนวนชั่วโมงที่ต้องการ/สัปดาห์</Label>
                    <Input
                      id="desired_hours"
                      type="number"
                      min="1"
                      max="20"
                      value={formData.desired_hours}
                      onChange={(e) => handleChange("desired_hours", e.target.value)}
                      placeholder="เช่น 2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="desired_start_date">วันที่ต้องการเริ่มเรียน</Label>
                    <Input
                      id="desired_start_date"
                      type="date"
                      value={formData.desired_start_date}
                      onChange={(e) => handleChange("desired_start_date", e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="desired_course">คอร์สที่สนใจ</Label>
                <Select value={formData.desired_course} onValueChange={(v) => handleChange("desired_course", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="เลือกคอร์ส" />
                  </SelectTrigger>
                  <SelectContent>
                    {COURSE_OPTIONS.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Teaching Style & Parent Concerns */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">ความคาดหวังของผู้ปกครอง</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>สไตล์การสอนของคุณครู</Label>
                <p className="text-xs text-muted-foreground mb-2">เลือกได้มากกว่า 1 ข้อ</p>
                <div className="space-y-2">
                  {TEACHING_STYLE_OPTIONS.map((style) => (
                    <label key={style} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.teaching_style.includes(style)}
                        onCheckedChange={(checked) => handleTeachingStyleToggle(style, !!checked)}
                      />
                      <span className="text-sm">{style}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>ปัญหาที่ผู้ปกครองเป็นกังวล</Label>
                <p className="text-xs text-muted-foreground mb-2">เลือกได้มากกว่า 1 ข้อ</p>
                <div className="space-y-2">
                  {PARENT_CONCERNS_OPTIONS.map((concern) => (
                    <label key={concern} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={formData.parent_concerns.includes(concern)}
                        onCheckedChange={(checked) => handleParentConcernToggle(concern, !!checked)}
                      />
                      <span className="text-sm">{concern}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label htmlFor="parent_concerns_other">ปัญหาที่ผู้ปกครองเป็นกังวล อื่น ๆ</Label>
                <Textarea
                  id="parent_concerns_other"
                  value={formData.parent_concerns_other}
                  onChange={(e) => handleChange("parent_concerns_other", e.target.value)}
                  placeholder="ระบุปัญหาอื่น ๆ ที่ไม่มีในรายการ"
                  maxLength={500}
                  rows={2}
                />
              </div>
              <div>
                <Label htmlFor="parent_expectations_trial">ความคาดหวังจากการทดลองเรียน</Label>
                <Textarea
                  id="parent_expectations_trial"
                  value={formData.parent_expectations_trial}
                  onChange={(e) => handleChange("parent_expectations_trial", e.target.value)}
                  placeholder="สิ่งที่คาดหวังจากคลาสทดลองเรียน"
                  maxLength={1000}
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="parent_expectations_fishsix">ความคาดหวังจาก Fishsix</Label>
                <Textarea
                  id="parent_expectations_fishsix"
                  value={formData.parent_expectations_fishsix}
                  onChange={(e) => handleChange("parent_expectations_fishsix", e.target.value)}
                  placeholder="สิ่งที่คาดหวังจากการเรียนกับ Fishsix"
                  maxLength={1000}
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="additional_pain_points">ข้อมูลเพิ่มเติม</Label>
                <Textarea
                  id="additional_pain_points"
                  value={formData.additional_pain_points}
                  onChange={(e) => handleChange("additional_pain_points", e.target.value)}
                  placeholder="ข้อมูลอื่นๆ ที่อยากให้ทีมงานทราบ"
                  maxLength={1000}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
            {isSubmitting ? "กำลังส่ง..." : "ส่งข้อมูลสมัครทดลองเรียน"}
          </Button>
        </form>
      </main>
    </div>
  );
};

export default PublicTrialFormPage;
